"use client";

import { useCallback } from "react";
import { validateFields } from "@/lib/utils";
import { FormState, VariantConfig, CategoryItem, NestedItem, Section } from "../types";
import {
  showErrorToast,
  showSuccessToast,
  showValidationErrorToast,
  mapFieldsConfigToFormConfig,
  extractCategoriesFromResponse,
  prepareSectionPreviewData,
} from "../utils";

interface UseNestedItemActionsProps {
  selectedSection: Section;
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  pageComponentId?: string;
  variantConfig: VariantConfig | null;
  nestedItemFieldsConfig: any[];
  currentUserId?: string | number;
  onSave: (section: Section) => void;
  fetchData: (forceFetch?: boolean) => Promise<void>;
}

// ============================================================================
// Helper: Build nested item fields
// ============================================================================
const buildNestedFields = (
  fieldsConfig: any[],
  nestedItem?: NestedItem
): Record<string, any> => {
  return fieldsConfig.reduce(
    (acc, field) => ({
      ...acc,
      [field.fieldName]: nestedItem?.[field.fieldName] ?? field.value ?? "",
    }),
    {}
  );
};

// ============================================================================
// Helper: Create form reset state
// ============================================================================
const createResetState = (defaults: Record<string, any>): Partial<FormState> => ({
  formFields: defaults,
  selectedItemType: null,
  selectedNestedId: null,
  editingItemId: null,
  formFieldErrors: {},
  formFieldMessages: {},
});

// ============================================================================
// Main Hook
// ============================================================================
export const useNestedItemActions = ({
  selectedSection,
  formState,
  setFormState,
  pageComponentId,
  variantConfig,
  nestedItemFieldsConfig,
  currentUserId,
  onSave,
  fetchData,
}: UseNestedItemActionsProps) => {
  const createNestedItemMethod = (variantConfig as any)?.createNestedItemMethod;
  const updateNestedItemMethod = (variantConfig as any)?.updateNestedItemMethod;
  const deleteNestedItemMethod = (variantConfig as any)?.deleteNestedItemMethod;
  const nestedItemParentIdFieldName =
    (variantConfig as any)?.nestedItemParentIdFieldName || "category_id";
  const fetchCategoriesMethod = (variantConfig as any)?.fetchCategoriesMethod;
  const childItemsFieldName = (variantConfig as any)?.childItemsFieldName || "categories";

  // ---------------------------------------------------------------------------
  // Helper: Update Preview
  // ---------------------------------------------------------------------------
  const updatePreview = async () => {
    if (!onSave || !fetchCategoriesMethod || !pageComponentId) return;

    try {
      const response = await fetchCategoriesMethod(Number(pageComponentId));
      const { categories: categoriesList, componentData } = extractCategoriesFromResponse(
        response,
        childItemsFieldName
      );

      const mergedData = { ...componentData, ...formState.componentFields };
      const id = componentData.id || selectedSection.componentId || 0;

      onSave(
        prepareSectionPreviewData(selectedSection, id, { ...mergedData, id }, categoriesList, childItemsFieldName)
      );
    } catch (error) {
      console.error("Failed to fetch updated data for preview:", error);
    }
  };

  // ---------------------------------------------------------------------------
  // Nested Item Click Handler
  // ---------------------------------------------------------------------------
  const handleNestedItemClick = useCallback(
    (category: CategoryItem, semester: NestedItem) => {
      const nestedFields = buildNestedFields(nestedItemFieldsConfig, semester);

      setFormState((prev) => ({
        ...prev,
        selectedItemType: "nested",
        selectedCategoryId: category.id,
        selectedNestedId: semester.id,
        selectedItemId: null,
        editingItemId: String(semester.id),
        formFields: nestedFields,
        currentFieldsConfig: mapFieldsConfigToFormConfig(nestedItemFieldsConfig),
        formFieldErrors: {},
        formFieldMessages: {},
      }));
    },
    [nestedItemFieldsConfig, setFormState]
  );

  // ---------------------------------------------------------------------------
  // New Nested Item Handler
  // ---------------------------------------------------------------------------
  const handleNewNestedItem = useCallback(
    (category: CategoryItem) => {
      const defaults = buildNestedFields(nestedItemFieldsConfig);

      setFormState((prev) => ({
        ...prev,
        selectedItemType: "nested",
        selectedCategoryId: category.id,
        selectedNestedId: null,
        selectedItemId: null,
        editingItemId: null,
        formFields: { ...defaults },
        currentFieldsConfig: [...mapFieldsConfigToFormConfig(nestedItemFieldsConfig)],
        formFieldErrors: {},
        formFieldMessages: {},
      }));
    },
    [nestedItemFieldsConfig, setFormState]
  );

  // ---------------------------------------------------------------------------
  // Nested Item Save Handler
  // ---------------------------------------------------------------------------
  const handleNestedItemSave = useCallback(async () => {
    if (!formState.selectedCategoryId) {
      showErrorToast("Please select a category first");
      return;
    }

    const { errors, messages } = validateFields(formState.currentFieldsConfig, formState.formFields);
    setFormState((prev) => ({ ...prev, formFieldErrors: errors, formFieldMessages: messages }));

    if (Object.keys(errors).length > 0) {
      showValidationErrorToast();
      return;
    }

    try {
      const { id, ...nestedItemData } = formState.formFields;
      const isUpdate = formState.editingItemId && !String(formState.editingItemId).startsWith("temp_");

      const payload = {
        ...nestedItemData,
        [nestedItemParentIdFieldName]: formState.selectedCategoryId,
        component_id: Number(pageComponentId),
        ...(isUpdate ? { updated_by: currentUserId } : { created_by: currentUserId }),
      };

      // Save operation using object mapping
      const saveOperations = {
        update: {
          canExecute: () => isUpdate && updateNestedItemMethod && formState.editingItemId,
          execute: async () => {
            await updateNestedItemMethod!(Number(formState.editingItemId), payload);
            showSuccessToast("Semester updated successfully");
          },
        },
        create: {
          canExecute: () => !isUpdate && createNestedItemMethod,
          execute: async () => {
            const response = await createNestedItemMethod!(payload);
            const responseId = response?.data?.id || response?.id;
            responseId &&
              setFormState((prev) => ({
                ...prev,
                editingItemId: String(responseId),
                selectedNestedId: responseId,
              }));
            showSuccessToast("Semester created successfully");
          },
        },
      };

      const operation = Object.values(saveOperations).find((op) => op.canExecute());
      await operation?.execute();

      // Reset form and refresh
      const defaults = buildNestedFields(nestedItemFieldsConfig);
      setFormState((prev) => ({ ...prev, ...createResetState(defaults) }));
      await updatePreview();
      await fetchData(true);
    } catch (error: any) {
      showErrorToast(error?.message || "Failed to save semester");
    }
  }, [
    formState.selectedCategoryId,
    formState.currentFieldsConfig,
    formState.formFields,
    formState.editingItemId,
    formState.componentFields,
    pageComponentId,
    currentUserId,
    nestedItemParentIdFieldName,
    nestedItemFieldsConfig,
    createNestedItemMethod,
    updateNestedItemMethod,
    fetchCategoriesMethod,
    childItemsFieldName,
    selectedSection,
    onSave,
    fetchData,
    setFormState,
  ]);

  // ---------------------------------------------------------------------------
  // Nested Item Delete Handler
  // Note: Confirmation should be handled by caller using DeleteDialog
  // ---------------------------------------------------------------------------
  const handleNestedItemDelete = useCallback(
    async (category: CategoryItem, semester: NestedItem) => {
      if (!deleteNestedItemMethod) return;

      try {
        await deleteNestedItemMethod(semester.id);
        showSuccessToast("Semester deleted successfully");

        // Clear form if deleting selected nested item
        formState.selectedNestedId === semester.id &&
          setFormState((prev) => ({
            ...prev,
            selectedItemType: null,
            selectedNestedId: null,
            editingItemId: null,
            formFields: {},
          }));

        await updatePreview();
        await fetchData(true);
      } catch (error: any) {
        showErrorToast(error?.message || "Failed to delete semester");
      }
    },
    [deleteNestedItemMethod, formState.selectedNestedId, formState.componentFields, fetchCategoriesMethod, childItemsFieldName, selectedSection, onSave, pageComponentId, fetchData, setFormState]
  );

  // ---------------------------------------------------------------------------
  // Toggle Nested Expand Handler
  // ---------------------------------------------------------------------------
  const toggleNestedExpand = useCallback(
    (nestedId: number) => {
      setFormState((prev) => {
        const newSet = new Set(prev.expandedNestedIds);
        newSet.has(nestedId) ? newSet.delete(nestedId) : newSet.add(nestedId);
        return { ...prev, expandedNestedIds: newSet };
      });
    },
    [setFormState]
  );

  return {
    handleNestedItemClick,
    handleNewNestedItem,
    handleNestedItemSave,
    handleNestedItemDelete,
    toggleNestedExpand,
  };
};
